import type { Feature } from "@visioni/a3s-features";
import type { SupportUserStatus } from "./UserContext";
import { holdsFeature, type TenantFeatures } from "./tenant-features";

/**
 * Access to a surface that needs both a support user and a hire feature.
 *
 * Three states, not a boolean, for the reason
 * {@link ../test-signals/access.ts} gives: a caller that cannot tell "still
 * settling" from "not entitled" denies a legitimate support user on a cold
 * deep-link. Nav entries read `=== "granted"`; a whole
 * page switches on all three.
 */
export type SupportFeatureAccess = "pending" | "granted" | "denied";

/**
 * Resolve access from the two answers it depends on, either of which can still
 * be in flight.
 *
 * Unknown is checked before entitlement, so neither unsettled auth nor an
 * unread camera list is mistaken for a missing hire. A failed feature read
 * counts as unknown rather than denied: the camera query retries on focus and
 * reconnect, so a transient failure resolves itself, whereas telling a support
 * user they lack access is the bug `DumpingSearch.web.tsx` documents.
 */
export function resolveSupportFeatureAccess(
  supportStatus: SupportUserStatus,
  features: TenantFeatures,
  feature: Feature,
): SupportFeatureAccess {
  if (supportStatus === "pending" || features.status !== "ready") return "pending";
  if (supportStatus !== "granted") return "denied";
  return holdsFeature(features, feature) ? "granted" : "denied";
}
